import { useEffect, useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { MegaFooter } from '../components/public/MegaFooter';

/**
 * Public Layout — Lightweight layout for unauthenticated, SEO-optimized pages.
 * Keeps public routes fast and independent from app dashboard bundles.
 */
export function PublicLayout() {
  const location = useLocation();
  const isHome = location.pathname === '/';
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Initialize state
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!isMobileMenuOpen) {
      document.body.style.overflow = '';
      return;
    }
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  return (
    <div className="min-h-screen bg-[#FAFAF9] flex flex-col relative">
      {/* Lightweight Public Navbar */}
      <header 
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled 
            ? 'bg-white/80 backdrop-blur-md border-b border-stone-200/60 py-0' 
            : 'bg-transparent border-b border-transparent py-1'
        }`}
      >
        <div className="max-w-[1200px] mx-auto px-4 md:px-6 h-16 flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-2.5 group">
            <div className={`w-8 h-8 rounded-lg overflow-hidden shadow-sm flex items-center justify-center transition-opacity hover:opacity-90 border border-stone-200/60 ${isScrolled ? 'bg-stone-50' : 'bg-white/50 backdrop-blur-sm'}`}>
              <img src="/logo.png" alt="Solo OS" width={32} height={32} className="w-full h-full object-cover mix-blend-multiply" />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[17px] font-bold text-stone-900 tracking-tight">
                Solo OS
              </span>
            </div>
          </Link>

          <nav className="hidden md:flex items-center gap-1.5">
            <Link
              to="/"
              className={`text-[14px] font-medium px-4 py-2 rounded-full transition-all duration-200 border ${isHome ? 'text-stone-900 bg-white border-stone-200 shadow-[0_2px_10px_-3px_rgba(0,0,0,0.07)]' : 'text-stone-500 border-transparent hover:text-stone-900 hover:bg-stone-100/60'}`}
            >
              Home
            </Link>
            {/* Was a hover-only dropdown driven by a <button> with no click
                handler, so it was unreachable by keyboard and invisible to
                touch. It is now a plain link to the /tools index, which is
                also a real crawlable page rather than markup on every page. */}
            <Link
              to="/tools"
              className={`text-[14px] font-medium px-4 py-2 rounded-full transition-all duration-200 border ${location.pathname.startsWith('/tools') ? 'text-stone-900 bg-white border-stone-200 shadow-[0_2px_10px_-3px_rgba(0,0,0,0.07)]' : 'text-stone-500 border-transparent hover:text-stone-900 hover:bg-stone-100/60'}`}
            >
              Tools
            </Link>
            <Link
              to="/micro-tools"
              className={`text-[14px] font-medium px-4 py-2 rounded-full transition-all duration-200 border ${location.pathname.startsWith('/micro-tools') ? 'text-stone-900 bg-white border-stone-200 shadow-[0_2px_10px_-3px_rgba(0,0,0,0.07)]' : 'text-stone-500 border-transparent hover:text-stone-900 hover:bg-stone-100/60'}`}
            >
              Micro Tools
            </Link>
            <Link
              to="/resources"
              className={`text-[14px] font-medium px-4 py-2 rounded-full transition-all duration-200 border ${location.pathname.startsWith('/resources') ? 'text-stone-900 bg-white border-stone-200 shadow-[0_2px_10px_-3px_rgba(0,0,0,0.07)]' : 'text-stone-500 border-transparent hover:text-stone-900 hover:bg-stone-100/60'}`}
            >
              Resources
            </Link>
            <Link
              to="/contact"
              className={`text-[14px] font-medium px-4 py-2 rounded-full transition-all duration-200 border ${location.pathname.startsWith('/contact') ? 'text-stone-900 bg-white border-stone-200 shadow-[0_2px_10px_-3px_rgba(0,0,0,0.07)]' : 'text-stone-500 border-transparent hover:text-stone-900 hover:bg-stone-100/60'}`}
            >
              Contact
            </Link>
            <Link
              to="/faq"
              className={`text-[14px] font-medium px-4 py-2 rounded-full transition-all duration-200 border ${location.pathname.startsWith('/faq') ? 'text-stone-900 bg-white border-stone-200 shadow-[0_2px_10px_-3px_rgba(0,0,0,0.07)]' : 'text-stone-500 border-transparent hover:text-stone-900 hover:bg-stone-100/60'}`}
            >
              FAQ
            </Link>
            
            
          </nav>

          <div className="flex items-center gap-3">
            <div className="hidden md:block">
              <Link
                to="/tools/upi-invoice-generator-india"
                className="solo-btn solo-btn--primary !h-10 !px-5 !text-[13px]"
              >
                Create an invoice
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 6l6 6-6 6"/></svg>
              </Link>
            </div>
            <button
              type="button"
              onClick={() => setIsMobileMenuOpen(prev => !prev)}
              className="md:hidden inline-flex h-10 w-10 items-center justify-center rounded-lg border border-stone-200 bg-white text-stone-700"
              aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={isMobileMenuOpen}
            >
              {isMobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {isMobileMenuOpen && (
          <div className="md:hidden fixed inset-x-0 top-16 bottom-0 z-40">
            <button
              type="button"
              aria-label="Close mobile menu overlay"
              onClick={() => setIsMobileMenuOpen(false)}
              className="absolute inset-0 bg-stone-900/20"
            />
            <div className="relative max-w-[1200px] mx-3 mt-2 rounded-2xl border border-stone-200/80 bg-white/95 backdrop-blur-sm shadow-xl max-h-[calc(100vh-5.5rem)] overflow-y-auto px-4 py-3 space-y-1">
              <Link
                to="/"
                className={`block rounded-lg px-3 py-2 text-[14px] font-medium transition-colors ${isHome ? 'bg-stone-100 text-stone-900' : 'text-stone-600 hover:bg-stone-100 hover:text-stone-900'}`}
              >
                Home
              </Link>
              <Link
                to="/tools"
                className={`block rounded-lg px-3 py-2 text-[14px] font-medium transition-colors ${location.pathname.startsWith('/tools') ? 'bg-stone-100 text-stone-900' : 'text-stone-600 hover:bg-stone-100 hover:text-stone-900'}`}
              >
                Tools
              </Link>
              <Link
                to="/micro-tools"
                className={`block rounded-lg px-3 py-2 text-[14px] font-medium transition-colors ${location.pathname.startsWith('/micro-tools') ? 'bg-stone-100 text-stone-900' : 'text-stone-600 hover:bg-stone-100 hover:text-stone-900'}`}
              >
                Micro Tools
              </Link>
              <Link
                to="/resources"
                className={`block rounded-lg px-3 py-2 text-[14px] font-medium transition-colors ${location.pathname.startsWith('/resources') ? 'bg-stone-100 text-stone-900' : 'text-stone-600 hover:bg-stone-100 hover:text-stone-900'}`}
              >
                Resources
              </Link>
              <Link
                to="/contact"
                className={`block rounded-lg px-3 py-2 text-[14px] font-medium transition-colors ${location.pathname.startsWith('/contact') ? 'bg-stone-100 text-stone-900' : 'text-stone-600 hover:bg-stone-100 hover:text-stone-900'}`}
              >
                Contact
              </Link>
              <Link
                to="/faq"
                className={`block rounded-lg px-3 py-2 text-[14px] font-medium transition-colors ${location.pathname.startsWith('/faq') ? 'bg-stone-100 text-stone-900' : 'text-stone-600 hover:bg-stone-100 hover:text-stone-900'}`}
              >
                FAQ
              </Link>
              <Link
                to="/tools/upi-invoice-generator-india"
                className="solo-btn solo-btn--primary w-full !h-9 !text-[12px] mt-2 justify-center"
              >
                Create an invoice
                <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 6l6 6-6 6"/></svg>
              </Link>
            </div>
          </div>
        )}
      </header>

      {/* Page Content */}
      <main className={`flex-1 flex flex-col ${!isHome ? 'pt-16' : ''}`}>
        <Outlet />
      </main>

      {/* Mega Footer */}
      <MegaFooter />
    </div>
  );
}
